import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import Konva from 'konva';
import { CanvasGuides } from '../types';
import './CoverPreview.css';

interface CoverPreviewProps {
  stageRef: React.RefObject<Konva.Stage | null>;
  guides: CanvasGuides;
  stageWidth: number;
  stageHeight: number;
  onClose: () => void;
}

const cropImage = (
  url: string, sx: number, sy: number, sw: number, sh: number,
): Promise<string> =>
  new Promise(resolve => {
    const c = document.createElement('canvas');
    c.width = Math.max(1, sw);
    c.height = Math.max(1, sh);
    const ctx = c.getContext('2d')!;
    const img = new window.Image();
    img.onload = () => { ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh); resolve(c.toDataURL()); };
    img.src = url;
  });

type View = '3d' | 'flat';

/* ─── 3D Book ─────────────────────────────────────────────────────── */
interface Book3DProps {
  front: string; spine: string;
  frontW: number; spineW: number; bookH: number;
}

const Book3D: React.FC<Book3DProps> = ({ front, spine, frontW, spineW, bookH }) => {
  const [rot, setRot] = useState({ x: 8, y: 28 });
  const sceneRef = useRef<HTMLDivElement>(null);
  const animRef  = useRef<number>(0);
  const targetRef = useRef({ x: 8, y: 28 });
  const currentRef = useRef({ x: 8, y: 28 });

  // Smooth lerp toward target
  useEffect(() => {
    const tick = () => {
      const t = currentRef.current;
      const g = targetRef.current;
      const nx = t.x + (g.x - t.x) * 0.1;
      const ny = t.y + (g.y - t.y) * 0.1;
      if (Math.abs(nx - t.x) > 0.01 || Math.abs(ny - t.y) > 0.01) {
        currentRef.current = { x: nx, y: ny };
        setRot({ x: nx, y: ny });
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = sceneRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2); // -1 to 1
    const dy = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2); // -1 to 1
    targetRef.current = { x: 8 - dy * 12, y: 28 + dx * 18 };
  }, []);

  const handleMouseLeave = useCallback(() => {
    targetRef.current = { x: 8, y: 28 };
  }, []);

  const d = Math.max(spineW, 8); // minimum visible depth

  return (
    <div
      ref={sceneRef}
      className="cprev-3d-scene"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Drop-shadow lives OUTSIDE the preserve-3d tree so it renders correctly */}
      <div className="cprev-book-shadow-wrap" style={{ perspective: '1400px' }}>
        <div
          className="cprev-book-3d"
          style={{
            width: frontW,
            height: bookH,
            transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
          }}
        >
          {/* ── Front face (z = 0) ── */}
          <div className="cprev-face cprev-face-front" style={{ width: frontW, height: bookH }}>
            <img src={front} alt="Front" style={{ width: '100%', height: '100%', display: 'block' }} />
            {/* Binding crease at left edge */}
            <div className="cprev-binding-crease" />
          </div>

          {/* ── Spine face — perpendicular to front via rotateY(-90°) around right edge ── */}
          <div
            className="cprev-face cprev-face-spine"
            style={{ width: d, height: bookH, left: -d }}
          >
            <img src={spine} alt="Spine"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <div className="cprev-spine-light" />
          </div>

          {/* ── Pages edge (right side of book) ── */}
          <div
            className="cprev-face cprev-face-pages"
            style={{ width: d, height: bookH, left: frontW }}
          />

          {/* ── Top edge ── */}
          <div
            className="cprev-face cprev-face-top"
            style={{ width: frontW, height: d, top: -d }}
          />
        </div>
      </div>

      {/* Ground shadow (DOM layer, outside 3d tree) */}
      <div className="cprev-ground" style={{ width: frontW + d }} />
    </div>
  );
};

/* ─── Main component ───────────────────────────────────────────────── */
const CoverPreview: React.FC<CoverPreviewProps> = ({
  stageRef, guides, stageHeight, onClose,
}) => {
  const [front, setFront] = useState<string | null>(null);
  const [spine, setSpine] = useState<string | null>(null);
  const [back,  setBack]  = useState<string | null>(null);
  const [view,  setView]  = useState<View>('3d');

  const { bleedPx, backWidthPx, spineWidthPx, frontStartPx } = guides;
  const printH = stageHeight - bleedPx * 2;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const url = stage.toDataURL({ pixelRatio: 1 });
    Promise.all([
      cropImage(url, frontStartPx,                bleedPx, backWidthPx,  printH),
      cropImage(url, frontStartPx - spineWidthPx, bleedPx, spineWidthPx, printH),
      cropImage(url, bleedPx,                     bleedPx, backWidthPx,  printH),
    ]).then(([f, s, b]) => { setFront(f); setSpine(s); setBack(b); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxH   = 480;
  const scale  = maxH / printH;
  const frontW = Math.round(backWidthPx  * scale);
  const spineW = Math.round(spineWidthPx * scale);
  const bookH  = maxH;
  const loading = !front || !spine || !back;

  return (
    <div className="cprev-overlay" onClick={onClose}>
      <div className="cprev-modal" onClick={e => e.stopPropagation()}>

        <div className="cprev-header">
          <span className="cprev-title">Cover Preview</span>
          <div className="cprev-tabs">
            <button className={`cprev-tab${view === '3d'   ? ' active' : ''}`} onClick={() => setView('3d')}>3D Mockup</button>
            <button className={`cprev-tab${view === 'flat' ? ' active' : ''}`} onClick={() => setView('flat')}>Flat View</button>
          </div>
          <button className="cprev-close" onClick={onClose}>
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="cprev-body">
          {loading ? (
            <div className="cprev-loading">
              <div className="cprev-spinner" />
              <span>Generating preview…</span>
            </div>

          ) : view === '3d' ? (
            <Book3D front={front!} spine={spine!} frontW={frontW} spineW={spineW} bookH={bookH} />

          ) : (
            <div className="cprev-flat-scene">
              {([
                { label: 'Back Cover',  src: back!,  w: frontW,               slim: false },
                { label: 'Spine',       src: spine!, w: Math.max(40, spineW), slim: spineW < 20 },
                { label: 'Front Cover', src: front!, w: frontW,               slim: false },
              ]).map(({ label, src, w, slim }) => (
                <div key={label} className="cprev-flat-panel">
                  <div className="cprev-flat-img" style={{ width: w, height: bookH }}>
                    <img src={src} alt={label}
                      style={{ width: '100%', height: '100%', display: 'block', objectFit: slim ? 'contain' : 'cover' }} />
                  </div>
                  <span className="cprev-flat-label">{label}</span>
                  {slim && <span className="cprev-flat-note">Thin spine</span>}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default CoverPreview;